/** The map layers a viewer can toggle on/off (vital-only feeds have no map layer). */
export interface ToggleDef {
  id: string;
  label: string;
}

export const MAP_TOGGLES: ToggleDef[] = [
  { id: 'subway', label: 'Subway' },
  { id: 'subwaymap', label: 'Subway Map' },
  { id: 'buses', label: 'Buses' },
  { id: 'lirr', label: 'LIRR' },
  { id: 'lirrmap', label: 'LIRR Map' },
  { id: 'mnr', label: 'Metro-North' },
  { id: 'mnrmap', label: 'MNR Map' },
  { id: 'path', label: 'PATH' },
  { id: 'pathmap', label: 'PATH Map' },
  { id: 'ferry', label: 'Ferry' },
  { id: 'citibike', label: 'Citi Bike' },
  { id: 'c311', label: '311' },
  { id: 'cams', label: 'Cams' },
  { id: 'radar', label: 'Radar' },
  { id: 'traffic', label: 'Traffic' },
  { id: 'linknyc', label: 'LinkNYC' },
  { id: 'flights', label: 'Flights' },
  { id: 'floodnet', label: 'Flooding' },
  { id: 'harbor', label: 'Harbor Buoys' },
  { id: 'ene', label: 'Elev/Esc' },
  // { id: 'ais', label: 'Ships' }, // ships disabled for launch
  { id: 'power_outages', label: 'Power Outages' },
  { id: 'grid_load', label: 'Power Cables' },
  { id: 'location', label: 'My Location' },
];

/** Layer IDs that are hidden by default. */
export const DEFAULT_HIDDEN: readonly string[] = ['linknyc', 'citibike', 'traffic'];

/** Tracks which layers are hidden and notifies subscribers on change. */
export class Visibility {
  private hidden: Set<string>;
  private listeners = new Set<() => void>();

  constructor(initialHidden: readonly string[] = DEFAULT_HIDDEN) {
    this.hidden = new Set(initialHidden);
  }

  isVisible(id: string): boolean {
    return !this.hidden.has(id);
  }
  /** Snapshot of the hidden ids — used for persistence. */
  hiddenIds(): string[] {
    return [...this.hidden];
  }
  toggle(id: string): void {
    if (this.hidden.has(id)) this.hidden.delete(id);
    else this.hidden.add(id);
    for (const cb of this.listeners) cb();
  }
  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }
}

const STORAGE_KEY = 'vitals.hiddenLayers';
const TRAFFIC_DEFAULT_MIGRATION_KEY = 'vitals.trafficDefaultApplied';
// Tycoon visibility is persisted by the same store but intentionally omitted
// from MAP_TOGGLES: these controls exist only inside the alternate view.
const KNOWN_IDS = new Set([...MAP_TOGGLES.map((t) => t.id), 'tycoon.surface', 'tycoon.underground', 'tycoon.buildings']);

/** Read persisted hidden-layer ids, falling back to DEFAULT_HIDDEN. Unknown ids are dropped. */
export function loadHidden(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [...DEFAULT_HIDDEN];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...DEFAULT_HIDDEN];
    return parsed.filter((id): id is string => typeof id === 'string' && KNOWN_IDS.has(id));
  } catch {
    return [...DEFAULT_HIDDEN];
  }
}

/** Persist the current hidden set; ignores storage failures (private mode, quota). */
export function saveHidden(hidden: readonly string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...hidden]));
  } catch {
    // best-effort persistence
  }
}

/** Build a Visibility seeded from storage that saves itself on every change. */
export function createPersistentVisibility(unavailableIds: readonly string[] = []): Visibility {
  let hidden = loadHidden();
  try {
    // Existing users already have a stored list that predates `traffic`; an
    // absent id used to mean visible. Apply the new off-by-default choice once,
    // then let normal persistence preserve an explicit opt-in thereafter.
    if (localStorage.getItem(TRAFFIC_DEFAULT_MIGRATION_KEY) === null) {
      if (!hidden.includes('traffic')) hidden = [...hidden, 'traffic'];
      localStorage.setItem(TRAFFIC_DEFAULT_MIGRATION_KEY, '1');
      saveHidden(hidden);
    }
  } catch {
    // Storage is best effort; DEFAULT_HIDDEN already keeps traffic off here.
  }
  const unavailable = unavailableIds.filter((id) => KNOWN_IDS.has(id));
  if (unavailable.some((id) => !hidden.includes(id))) {
    hidden = [...new Set([...hidden, ...unavailable])];
    saveHidden(hidden);
  }
  const vis = new Visibility(hidden);
  vis.onChange(() => saveHidden(vis.hiddenIds()));
  return vis;
}
